import { useQuery } from "@apollo/client";
import { useMemo } from "react";
import {
  ARCHIVED_SERVICE_CATEGORIES_QUERY,
  ARCHIVED_SERVICES_QUERY,
} from "../../../../graphql/services";
import type { ServiceRowData } from "../types";

interface UseArchivedServicesProps {
  salonId?: string | null;
}

export function useArchivedServices({ salonId }: UseArchivedServicesProps) {
  // Fetch archived categories
  const {
    data: archivedCategoriesData,
    loading: loadingCategories,
    error: errorCategories,
  } = useQuery(ARCHIVED_SERVICE_CATEGORIES_QUERY, {
    variables: { salonId: salonId || "" },
    skip: !salonId,
  });

  // Fetch archived services
  const {
    data: archivedServicesData,
    loading: loadingServices,
    error: errorServices,
  } = useQuery(ARCHIVED_SERVICES_QUERY, {
    variables: { salonId: salonId || "" },
    skip: !salonId,
  });

  const archivedCategories = archivedCategoriesData?.archivedServiceCategories || [];
  const archivedServicesFlat = archivedServicesData?.archivedServices || [];

  // Group archived services by category
  const archivedServicesByCategory = useMemo(() => {
    const grouped: Record<string, ServiceRowData[]> = {};

    archivedServicesFlat.forEach((service: any) => {
      const categoryId = service.category?.id;
      if (categoryId) {
        if (!grouped[categoryId]) {
          grouped[categoryId] = [];
        }
        grouped[categoryId].push({
          id: service.id,
          name: service.name,
          description: service.description,
          serviceCode: service.serviceCode,
          duration: service.duration,
          pricingMode: service.pricingMode,
          basePrice: service.basePrice,
          isActive: service.isActive,
          allowOnlineBooking: service.allowOnlineBooking,
          minPrice: service.displayPrice?.priceRange?.minPrice ?? undefined,
          maxPrice: service.displayPrice?.priceRange?.maxPrice ?? undefined,
          sortOrder: 0,
        });
      }
    });

    return grouped;
  }, [archivedServicesFlat]);

  // Get all archived services flattened with proper category info
  const allArchivedServices = useMemo(() => {
    return archivedServicesFlat
      .filter((service) => !service.isActive)
      .map((service) => ({
        id: service.id,
        name: service.name,
        description: service.description,
        serviceCode: service.serviceCode,
        duration: service.duration,
        pricingMode: service.pricingMode,
        basePrice: service.basePrice,
        isActive: service.isActive,
        allowOnlineBooking: service.allowOnlineBooking,
        minPrice: service.displayPrice?.priceRange?.minPrice ?? undefined,
        maxPrice: service.displayPrice?.priceRange?.maxPrice ?? undefined,
        categoryId: service.category?.id,
        categoryName: service.category?.name || "Unknown Category",
        categoryColor: service.category?.color || "#gray",
      }));
  }, [archivedServicesFlat]);

  const archivedCategoriesCount = archivedCategories.length;
  const archivedServicesCount = archivedServicesFlat.length;
  const hasArchivedItems = archivedCategoriesCount > 0 || archivedServicesCount > 0;
  const totalArchivedCount = archivedCategoriesCount + archivedServicesCount;

  return {
    archivedCategories,
    archivedServicesByCategory,
    allArchivedServices,
    archivedCategoriesCount,
    archivedServicesCount,
    hasArchivedItems,
    totalArchivedCount,
    loading: loadingCategories || loadingServices,
    error: errorCategories || errorServices,
  };
}
